import { useEffect, useState } from 'react'
import { deadlineFetch } from './customEndpointFetch'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

/** One clinic linked to a pet, as returned by /pets/linked-clinics. */
export interface LinkedClinic {
  id: number
  name: string | null
  email: string | null
  clinic_name: string | null
  hospital_name: string | null
  /** Which relationship branch linked it: directOwner | ownerVets | allPetsShare | specificShare */
  source: string
}

export interface PetLinkedClinicsResult {
  /**
   * pet id → linked clinics (ordered: owner linkage before shares).
   * null while loading or when the endpoint is unavailable — callers should
   * fall back to the legacy owner.vets resolution rather than render blanks.
   */
  clinicsByPetId: ReadonlyMap<number, LinkedClinic[]> | null
  isLoading: boolean
  error: string | null
}

// Module-level cache so the pets list, the dashboard "Recently Updated"
// datagrid, and the mobile card list share ONE fetch instead of three. Keyed
// by token so a user switch on a shared clinic machine can't serve the
// previous session's map (the same leakage class as the glucose-unit bug).
const CACHE_TTL_MS = 60 * 1000
let cache: {
  token: string
  data: Map<number, LinkedClinic[]>
  fetchedAt: number
} | null = null
let inflight: { token: string; promise: Promise<Map<number, LinkedClinic[]>> } | null = null

export const __resetPetLinkedClinicsCacheForTests = () => {
  cache = null
  inflight = null
}

const fetchLinkedClinics = (token: string | null): Promise<Map<number, LinkedClinic[]>> => {
  // Only dedup/cache token-bearing sessions. A cookie-only session (token null,
  // browser reopen) must NOT share the module cache — two different users on a
  // shared browser both have a null sessionStorage token, so a null-keyed cache
  // entry would leak one clinic's linked-pet PII to the other (L-019).
  if (token && inflight && inflight.token === token) {
    return inflight.promise
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  // JWT scheme, not Bearer — same recurring convention as the other
  // custom-endpoint callers in this codebase.
  if (token) headers.Authorization = `JWT ${token}`

  const promise = deadlineFetch(`${apiUrl}/pets/linked-clinics`, {
    headers,
    credentials: 'same-origin',
  })
    .then(async (resp) => {
      if (!resp.ok) {
        let detail = ''
        try {
          const body = await resp.json()
          detail = body?.error || body?.detail || ''
        } catch {
          /* ignore */
        }
        throw new Error(
          `Failed to load linked clinics${detail ? ` — ${detail}` : ` (HTTP ${resp.status})`}`,
        )
      }
      const data = await resp.json()
      const map = new Map<number, LinkedClinic[]>()
      const raw = data?.clinicsByPetId
      if (raw && typeof raw === 'object') {
        for (const [petIdStr, clinics] of Object.entries(raw)) {
          const petId = Number(petIdStr)
          if (!Number.isInteger(petId) || !Array.isArray(clinics)) continue
          map.set(
            petId,
            clinics
              .filter((c: any) => c && typeof c === 'object' && Number.isInteger(c.id))
              .map((c: any) => ({
                id: c.id,
                name: c.name ?? null,
                email: c.email ?? null,
                clinic_name: c.clinic_name ?? null,
                hospital_name: c.hospital_name ?? null,
                source: String(c.source ?? ''),
              })),
          )
        }
      }
      // Only persist the cache for token sessions (never under a null key).
      if (token) cache = { token, data: map, fetchedAt: Date.now() }
      return map
    })
    .finally(() => {
      if (token && inflight && inflight.token === token) inflight = null
    })

  if (token) inflight = { token, promise }
  return promise
}

/**
 * Authoritative pet→clinic map for the "Vet Account" column.
 *
 * Why this exists (Jun 06/26 issue list, repeat report): the column used to
 * resolve the clinic from `record.owner.vets`, which goes blank when the
 * relationship isn't hydrated to objects at the requested depth AND for pets
 * linked only via vet-shares — while the clinic detail page (authoritative
 * SQL endpoint) showed the link. Support searching by an owner's email could
 * not find the pet's clinic. This hook asks the backend's four-branch SQL
 * directly, so the list and the clinic page can never disagree again.
 */
export const usePetLinkedClinics = (): PetLinkedClinicsResult => {
  const token = sessionStorage.getItem('token')
  const cachedValid =
    cache !== null && cache.token === token && Date.now() - cache.fetchedAt < CACHE_TTL_MS

  const [clinicsByPetId, setClinicsByPetId] = useState<Map<number, LinkedClinic[]> | null>(
    cachedValid && cache ? cache.data : null,
  )
  const [isLoading, setIsLoading] = useState(!cachedValid)
  const [error, setError] = useState<string | null>(null)
  // Bumped when the window regains focus AFTER the cache expired, so a
  // long-lived mount (dashboard left open all day) refreshes the moment a
  // human is looking again — the TTL used to be checked only at mount, which
  // made "60s TTL" effectively unbounded staleness for the whole session
  // (P63-5). Focus-gated on purpose: a plain interval would turn every
  // mounted list into a 60-second poller for nobody.
  const [staleFocusTick, setStaleFocusTick] = useState(0)

  useEffect(() => {
    const onFocus = () => {
      const expired =
        cache === null || cache.token !== token || Date.now() - cache.fetchedAt >= CACHE_TTL_MS
      if (expired) setStaleFocusTick((t) => t + 1)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [token])

  useEffect(() => {
    // No `if (!token) return` — a cookie-only session (browser reopen) still
    // fetches via the same-origin cookie (L-019). `cachedValid` is already false
    // when token is null (cache.token is always a string), so the cache READ is
    // leak-safe; fetchLinkedClinics guards the WRITE path.
    if (cachedValid && cache) {
      setClinicsByPetId(cache.data)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    fetchLinkedClinics(token)
      .then((map) => {
        if (cancelled) return
        setClinicsByPetId(map)
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.message || 'Failed to load linked clinics.')
        // Keep null on error so callers degrade to the legacy owner-based
        // resolution rather than rendering every clinic as blank.
        setClinicsByPetId(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, staleFocusTick])

  return { clinicsByPetId, isLoading, error }
}
